/**
 * Tamanho do conteúdo — acessibilidade de verdade, não enfeite.
 *
 * Há gente na equipe que não enxerga bem, e a densidade que o resto do time
 * prefere para produzir (linha de 26 px, fonte de 12,5) é ilegível para elas. O
 * jeito de atender as duas é **escala por pessoa**, não um tamanho novo para
 * todo mundo.
 *
 * **A escala cresce a letra e a linha juntas.** Aumentar só a fonte dentro de
 * uma linha de altura fixa espremeria o texto e pioraria a leitura, que é
 * justamente a queixa. Crescendo as duas, vê-se menos linha por tela — é a
 * troca certa: quem precisa aceita rolar mais, quem não precisa fica onde está.
 *
 * **A barra de cima não escala.** Se ela crescesse junto, o menu comeria a tela
 * inteira no passo de 150% e sobraria menos espaço para o que interessa, que é
 * o conteúdo. Isto é o que diferencia daqui do zoom do navegador (Ctrl +), que
 * cresce tudo — e que continua funcionando por cima deste controle, para quem
 * preferir.
 *
 * Fica gravado no navegador da pessoa. Não vai para o banco: seria campo novo
 * na tabela `painel`, que é compartilhada com o Sistema de hoje.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react"

/** Quatro passos. O primeiro é o tamanho de hoje — ninguém é movido de lugar. */
export const PASSOS = [1, 1.15, 1.3, 1.5] as const
export type Escala = (typeof PASSOS)[number]

const CHAVE = "sistema-oc.escala"

type Ctx = {
  escala: Escala
  aumentar: () => void
  diminuir: () => void
  podeAumentar: boolean
  podeDiminuir: boolean
  /** Para anunciar a mudança: "conteúdo em 130%". */
  porcento: number
}

const Contexto = createContext<Ctx | null>(null)

function lerGravada(): Escala {
  try {
    const n = Number(localStorage.getItem(CHAVE))
    return (PASSOS as readonly number[]).includes(n) ? (n as Escala) : 1
  } catch {
    return 1
  }
}

export function ProvedorDeEscala({ children }: { children: ReactNode }) {
  const [escala, setEscala] = useState<Escala>(lerGravada)

  useEffect(() => {
    document.documentElement.style.setProperty("--escala", String(escala))
    try { localStorage.setItem(CHAVE, String(escala)) } catch { /* sem armazenamento */ }
  }, [escala])

  const indice = PASSOS.indexOf(escala)

  const aumentar = useCallback(
    () => setEscala((e) => PASSOS[Math.min(PASSOS.indexOf(e) + 1, PASSOS.length - 1)]!), [])
  const diminuir = useCallback(
    () => setEscala((e) => PASSOS[Math.max(PASSOS.indexOf(e) - 1, 0)]!), [])

  const valor = useMemo(
    () => ({
      escala,
      aumentar,
      diminuir,
      podeAumentar: indice < PASSOS.length - 1,
      podeDiminuir: indice > 0,
      porcento: Math.round(escala * 100),
    }),
    [escala, aumentar, diminuir, indice],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function usarEscala(): Ctx {
  const c = useContext(Contexto)
  if (!c) throw new Error("usarEscala fora do ProvedorDeEscala")
  return c
}
